// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// PasswordStrength — Visual password strength meter
// ================================================================
import React, { useMemo } from 'react';

function getStrength(password) {
  if (!password) return { score: 0, label: '', color: 'var(--border)' };

  let score = 0;
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
  if (/\d/.test(password)) score++;
  if (/[^a-zA-Z\d]/.test(password)) score++;
  // Bonus for length > 16
  if (password.length >= 16) score++;

  // Penalty for common patterns
  const common = ['password', '123456', 'qwerty', 'abc123', 'letmein', 'admin', 'welcome'];
  if (common.some((c) => password.toLowerCase().includes(c))) score = Math.max(score - 2, 0);

  // Map to 0-4 scale
  const level = Math.min(Math.floor(score * 4 / 6), 4);

  const levels = [
    { label: 'Very Weak', color: '#DC2626' },
    { label: 'Weak', color: '#F97316' },
    { label: 'Fair', color: '#D97706' },
    { label: 'Strong', color: '#16A34A' },
    { label: 'Very Strong', color: '#059669' },
  ];

  return { score: level, ...levels[level] };
}

/**
 * PasswordStrength
 *
 * Props:
 *   password   — the password string to evaluate
 *   show       — whether to show (default: true when password has content)
 *   style      — container style
 */
export default function PasswordStrength({ password = '', show, style }) {
  const strength = useMemo(() => getStrength(password), [password]);

  if (show === false || (!show && !password)) return null;

  return (
    <div className="password-strength" style={{ marginTop: 8, ...style }}>
      <div style={{ display: 'flex', gap: 3, marginBottom: 4 }}>
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            style={{
              flex: 1, height: 4, borderRadius: 2,
              background: i <= strength.score - 1 ? strength.color : 'var(--border)',
              transition: 'background 0.3s',
            }}
          />
        ))}
      </div>
      {password && (
        <div style={{ fontSize: 11, fontWeight: 600, color: strength.color }}>
          {strength.label}
        </div>
      )}
    </div>
  );
}

export { getStrength };
